import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { useAlert, useApi } from '@app/hooks';
import { TableBase } from '@app/components/common/table/table-base';
import Dialog from '@app/components/common/modals/dialog';
import { SwitchField, TextField } from '@app/components/common';

import { useColumns } from './columns';

type ShelvesCategory = EntityBase & { is_active?: boolean };

export const CategoryShelvesTable: FC = () => {
  const api = useApi();
  const { error } = useAlert();

  const [categories, setCategories] = useState<EntityBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<ShelvesCategory>();
  const [currentDialog, setCurrentDialog] = useState<'edit' | 'create'>();

  const fetchList = useCallback(() => {
    setLoading(true);
    api.guides.shelvesCategoryList().then(({ data }) => {
      setLoading(false);
      setCategories(data || []);
    });
  }, [api]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = (category: ShelvesCategory) => {
    setCurrentCategory(category);
    setCurrentDialog('edit');
  };

  const columns = useColumns({ onEdit });

  const onClose = () => {
    setCurrentCategory(undefined);
    setCurrentDialog(undefined);
  };

  const onSubmit = (category?: ShelvesCategory) => async () => {
    if (!category?.name) {
      error('Введите название');
      return;
    }
    setLoading(true);
    if (category.id) {
      await api.guides.shelvesCategoryUpdate({
        id: Number(category.id),
        name: category.name,
        is_active: !!category.is_active,
      });
    } else {
      await api.guides.shelvesCategoryCreate({
        name: category.name,
        is_active: category.is_active ?? true,
      });
    }
    fetchList();
    onClose();
  };

  return (
    <>
      <TableBase<EntityBase>
        title="Категории полок"
        data={categories}
        columns={columns}
        loading={loading}
        onCreateClick={() => {
          setCurrentCategory({ is_active: true });
          setCurrentDialog('create');
        }}
      />
      <Dialog
        open={!!currentDialog}
        onClose={onClose}
        title={currentDialog === 'edit' ? 'Редактирование категории' : 'Создание категории'}
      >
        <Flex gap={2} flexDirection="column">
          <TextField
            id="name"
            label="Название"
            required
            value={currentCategory?.name || ''}
            onChange={(value) =>
              setCurrentCategory({
                ...currentCategory,
                name: value,
              })
            }
          />
          <SwitchField
            label="Активна"
            value={currentCategory?.is_active}
            onChange={(value) =>
              setCurrentCategory({
                ...currentCategory,
                is_active: value,
              })
            }
            id="is_active"
          />
          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="outline" colorScheme="gray" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="solid" colorScheme="brand" onClick={onSubmit(currentCategory)}>
              Сохранить
            </Button>
          </Flex>
        </Flex>
      </Dialog>
    </>
  );
};

export default CategoryShelvesTable;
